// assets/custom-collection-filter.js

(function () {
  'use strict';

  const SELECTORS = {
    root:  '[data-component="collection-filter"]',
    track: '[data-filter-track]',
    chip:  '.filter-chips__link',
    prev:  '[data-filter-prev]',
    next:  '[data-filter-next]',
  };

  const instances = new Map();

  class CollectionFilter {
    constructor(el) {
      this.el             = el;
      this.sectionEl      = document.getElementById('shopify-section-' + (el.dataset.sectionId || '')) || el;
      this.track          = el.querySelector(SELECTORS.track);
      this.navEl          = this.track ? this.track.parentElement : null; // <nav> is the scroll container
      this.source         = el.dataset.source || 'manual-blocks';
      this.collection     = el.dataset.collection || '';
      this.collUrl        = el.dataset.collectionUrl || window.location.pathname;
      this.scrollable     = el.dataset.scrollable === 'true';
      this.currentEl      = el.querySelector('[data-filter-current]');
      this.noFilterPrefix = el.dataset.noFilterPrefix || '';
      this.megaNavLabel   = '';
      this._init();
    }

    _init() {
      if (this.source === 'from-megamenu') {
        this._renderFromMegamenu();
      } else {
        this._detectActive();
        this._updateCurrentDisplay();
        this._scrollActiveIntoView();
      }
      this._bindArrows();
      // Keep current-filter display in sync when product grid applies a client-side filter
      this._onFilterApplied = () => this._updateCurrentDisplay();
      document.addEventListener('hbk:filter:applied', this._onFilterApplied);
    }

    // Reads the matching mfb-col-config-filter <script> element (injected by the
    // header mega menu) and renders filter chips dynamically.
    _renderFromMegamenu() {
      if (!this.track) return;

      // Remove previously rendered megamenu chips to prevent duplication
      // when shopify:section:load fires after a Theme Editor setting change.
      this.track.querySelectorAll('[data-megamenu-chip]').forEach(li => li.remove());

      const handle   = CSS.escape(this.collection);
      const configEl = document.querySelector(`.mfb-col-config-filter[data-collection="${handle}"]`);

      let filters = [];
      if (configEl) {
        this.megaNavLabel = configEl.dataset.navLabel || '';
        try { filters = JSON.parse(configEl.textContent); } catch { filters = []; }
        if (!Array.isArray(filters)) filters = [];
      }

      // Only slots with a label actually render a chip.
      const renderable = filters.filter(f => f && f.label);

      // No filter slots on the mega panel for this collection → hide the whole row
      // (the section stays display:none from CSS since we never mark it ready) so the
      // collection page isn't cluttered with an empty "Filter" label.
      if (!renderable.length) {
        this._setReady(false);
        return;
      }

      renderable.forEach(f => {
        const li = document.createElement('li');
        li.className = 'filter-chips__item';
        li.dataset.megamenuChip = '';

        const url = this._buildFilterUrl(f.ns || '', f.key || '', f.value, f.ns === 'tag');
        const a   = document.createElement('a');
        a.href                 = url;
        a.className            = 'filter-chips__link';
        a.dataset.filterNs     = f.ns    || '';
        a.dataset.filterKey    = f.key   || '';
        a.dataset.filterValue  = f.value || '';
        a.dataset.isTag        = String(!f.ns || f.ns === 'tag');

        if (f.icon) {
          const img     = document.createElement('img');
          img.src       = f.icon;
          img.alt       = '';
          img.className = 'filter-chips__icon';
          img.loading   = 'lazy';
          img.setAttribute('aria-hidden', 'true');
          img.style.cssText = 'width:20px;height:20px;object-fit:contain;flex-shrink:0;';
          a.appendChild(img);
        }

        const span       = document.createElement('span');
        span.className   = 'filter-chips__label-text';
        span.textContent = f.label;
        a.appendChild(span);

        li.appendChild(a);
        this.track.appendChild(li);
      });

      this._setReady(true);
      this._detectActive();
      this._updateCurrentDisplay();
      this._scrollActiveIntoView();
    }

    // Reveals (or keeps hidden) the whole section. On the live site the section is
    // display:none by default (set in the section CSS for the from-megamenu source);
    // adding the ready class shows it. When there are no filters we never add it, so
    // the row stays hidden. In the Theme Editor the CSS never hides it, so this is a
    // no-op there and the merchant always sees the section.
    _setReady(isReady) {
      this.sectionEl?.classList.toggle('hbk-filter-chips--ready', isReady);
    }

    // Updates the inline current-filter display next to the label text.
    // Shows the active chip label, or "{prefix} {megaNavLabel}" when none active.
    _updateCurrentDisplay() {
      if (!this.currentEl) return;
      const active = this.el.querySelector(`${SELECTORS.chip}.is-active`);
      if (active) {
        const labelSpan = active.querySelector('.filter-chips__label-text');
        const text = labelSpan?.textContent.trim() || active.dataset.filterValue || '';
        this.currentEl.textContent = text ? ': ' + text : '';
      } else {
        const parts = [this.noFilterPrefix, this.megaNavLabel].filter(Boolean);
        this.currentEl.textContent = parts.length ? ': ' + parts.join(' ') : '';
      }
    }

    _buildFilterUrl(ns, key, value, isTag) {
      if (!value) return this.collUrl;
      const param = (isTag || !ns || ns === 'tag')
        ? `filter.p.tag=${encodeURIComponent(value)}`
        : `filter.p.m.${ns}.${key}=${encodeURIComponent(value)}`;
      return `${this.collUrl}?${param}`;
    }

    // Reads URL params to highlight the active filter chip. Works without
    // Storefront Filters API since it checks the raw query string directly.
    _detectActive() {
      const params       = new URLSearchParams(window.location.search);
      const hasAnyFilter = [...params.keys()].some(k => k.startsWith('filter.p.'));

      this.el.querySelectorAll(SELECTORS.chip).forEach(chip => {
        const ns    = chip.dataset.filterNs    || '';
        const key   = chip.dataset.filterKey   || '';
        const value = chip.dataset.filterValue || '';
        const isTag = chip.dataset.isTag === 'true';
        const isAll = chip.hasAttribute('data-filter-all');

        let active = false;
        if (isAll) {
          active = !hasAnyFilter;
        } else if (isTag || !ns || ns === 'tag') {
          active = params.get('filter.p.tag') === value;
        } else {
          active = params.get(`filter.p.m.${ns}.${key}`) === value;
        }

        if (active) {
          chip.classList.add('is-active');
          chip.setAttribute('aria-current', 'true');
        }
      });
    }

    _scrollActiveIntoView() {
      if (!this.scrollable || !this.track) return;
      const active    = this.track.querySelector(`${SELECTORS.chip}.is-active`);
      const scrollEl  = this.navEl || this.track; // nav is the scroll container
      if (!active || !scrollEl) return;
      requestAnimationFrame(() => {
        const scrollRect = scrollEl.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const offset = activeRect.left - scrollRect.left - (scrollRect.width - activeRect.width) / 2;
        scrollEl.scrollBy({ left: offset, behavior: 'smooth' });
      });
    }

    _bindArrows() {
      const prev = this.el.querySelector(SELECTORS.prev);
      const next = this.el.querySelector(SELECTORS.next);
      if (!this.track || (!prev && !next)) return;

      const scrollEl = this.navEl || this.track;
      const scrollBy = dir => {
        const gap    = parseInt(getComputedStyle(this.track).gap) || 8;
        const first  = this.track.querySelector('li');
        const amount = first ? first.offsetWidth + gap : 140;
        scrollEl.scrollBy({ left: dir * amount, behavior: 'smooth' });
      };

      prev?.addEventListener('click', () => scrollBy(-1));
      next?.addEventListener('click', () => scrollBy(1));
    }

    destroy() {
      if (this._onFilterApplied) {
        document.removeEventListener('hbk:filter:applied', this._onFilterApplied);
      }
    }
  }

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new CollectionFilter(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) {
        instances.get(el).destroy();
        instances.delete(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));

})();
